import { da, id } from 'date-fns/locale';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, Output, signal } from '@angular/core';
import { MaterialModule } from 'src/app/material.module';
import { IMiSimpleTableColumn, MiTableSimpleComponent } from 'src/app/shared/components/mi-table-simple/mi-table-simple.component';
import { StackDirectives } from 'src/app/shared/directives/stack-directives';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { PersonAcademicService } from '../../services/person-academic.service';
import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { ModalInfoAcademica } from '../modal-info-academica/modal-info-academica';
import { IListBox } from 'src/app/shared/interfaces/IClaveValor';
import { MiAdmPersonasAcademicos } from '../../interfaces/IAdmPersonAcademic';
import { FormBuilder, Validators } from '@angular/forms';
import { ConfirmDeleteDialogComponent } from 'src/app/shared/components/confirm-delete/confirm-delete.component';
import { BreakpointObserver } from '@angular/cdk/layout';
import { map, shareReplay, Subscription } from 'rxjs';

@Component({
  selector: 'app-info-academica',
  imports: [StackDirectives, MaterialModule, CommonModule, MiTableSimpleComponent],
  templateUrl: './info-academica.html',
  styleUrl: './info-academica.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InfoAcademica {
  private loader = inject(LoaderService);
  private toastr = inject(ToastrService);
  private personAcademicService = inject(PersonAcademicService);
  private ac = inject(ActivatedRoute);
  private dialog = inject(MatDialog);
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private bp = inject(BreakpointObserver);
  private sub = new Subscription();

  idEncrypt: any;
  monthsList: IListBox[];
  indInexp: boolean = false;

  @Input() codEmp!: string;
  @Input() tipIde!: string;

  isMobile = signal<boolean>(false);

  @Output() validChange = new EventEmitter<boolean>();

  readonly dataSource = signal<any[]>([]);
  readonly columns = signal<IMiSimpleTableColumn[]>([]);
  readonly displayedColumns = signal<string[]>([]);
  readonly filterColumns = signal<string[]>([]);

  form = this.fb.group({
    mesIni: [null],
    anoIni: [null],
    mesFin: [null],
    anoFin: [null],
    actEst: [false],
    idNivAca: ['', [Validators.required]],
    nomInst: ['', [Validators.required, Validators.maxLength(100)]],
    programa: ['', [Validators.required, Validators.maxLength(100)]]
  });

  ngOnInit() {
    this.ac.queryParams.subscribe((params) => {
      const rawId = params['q'];
      if (rawId) {
        this.idEncrypt = encodeURIComponent(rawId);
        this.monthsList = this.generateMonthsList();
        this.getDataAcademic(this.idEncrypt);

        const s = this.bp
          .observe(['(max-width: 768px)']) // ajusta el umbral a tu diseño
          .pipe(
            map((r) => r.matches),
            shareReplay({ bufferSize: 1, refCount: true })
          )
          .subscribe((isSmall) => this.isMobile.set(isSmall));

        this.sub.add(s);
      }
    });
  }

  getDataAcademic(id: string) {
    this.loader.show('Cargando niveles académicos...');
    this.personAcademicService.getById(id).subscribe({
      next: (response) => {
        this.loader.hide();

        // Emitir evento de validación
        if (response.data.length === 0) {
          this.validChange.emit(false);
        } else {
          this.validChange.emit(true);
        }

        this.indInexp = this.validateInexperiencia(response.data);
        const dataComplete = response.data.map((item) => {
          const nomMesIni =
            this.monthsList.length > 0 && item.mesInicio != null
              ? this.monthsList.find((c: any) => c.id === item.mesInicio?.toString())?.label + ' ' + item.anioInicio
              : '';
          const nomMesFin =
            this.monthsList.length > 0 && item.mesFin != null
              ? this.monthsList.find((c: any) => c.id === item.mesFin?.toString())?.label + ' ' + item.anioFin
              : '';

          const progNivAca = item.nivelAcademico
            ? `<strong>${item.programa}</strong> - ${item.nivelAcademico.descripcion}`
            : item.institucion;

          let periodo = '';
          const iniVacio = nomMesIni.trim().length === 0;
          const finVacio = nomMesFin.trim().length === 0;

          if (iniVacio && finVacio) {
            periodo = ''; // no mostrar nada
          } else if (!iniVacio && finVacio) {
            periodo = `${nomMesIni} - Actualmente estudiando`;
          } else {
            // Ambos presentes: muestra rango
            periodo = `${nomMesIni} - ${nomMesFin}`; // en dash para mejor tipografía
          }

          return {
            ...item,
            nomMesIni: nomMesIni,
            nomMesFin: nomMesFin,
            progNivAca: progNivAca,
            periodo: periodo
          };
        });

        this.dataSource.set(dataComplete);
        this.displayedColumns.set(['institucion', 'progNivAca', 'periodo']);
        this.filterColumns.set(['institucion', 'progNivAca', 'periodo']);
        this.columns.set([
          { field: 'institucion', header: 'Institución', sortable: true, bold: true, align: 'left' },
          { field: 'progNivAca', header: 'Programa', sortable: true, bold: true, type: 'html' },
          { field: 'periodo', header: 'Periodo', sortable: true, align: 'left' }
        ]);
      },
      error: (error) => {
        this.loader.hide();
        this.toastr.error('Error al cargar', error.message);
      }
    });
  }

  generateMonthsList(): IListBox[] {
    const months = [
      'Enero',
      'Febrero',
      'Marzo',
      'Abril',
      'Mayo',
      'Junio',
      'Julio',
      'Agosto',
      'Septiembre',
      'Octubre',
      'Noviembre',
      'Diciembre'
    ];

    return months.map((month, index) => ({
      label: month,
      id: (index + 1).toString()
    }));
  }

  openModalCreate() {
    this.dialog
      .open(ModalInfoAcademica, {
        width: '50%',
        maxWidth: '100vw',
        data: {
          idEncrypt: this.idEncrypt,
          codEmp: this.codEmp,
          tipIde: this.tipIde
        }
      })
      .afterClosed()
      .subscribe((response) => {
        this.getDataAcademic(this.idEncrypt);
      });
  }

  onEdit(row: MiAdmPersonasAcademicos) {
    this.dialog
      .open(ModalInfoAcademica, {
        width: '50%',
        maxWidth: '100vw',
        data: {
          idEncrypt: this.idEncrypt,
          codEmp: this.codEmp,
          tipIde: this.tipIde,
          academicData: row
        }
      })
      .afterClosed()
      .subscribe((response) => {
        this.getDataAcademic(this.idEncrypt);
      });
  }

  onDelete(row: any) {
    this.dialog
      .open(ConfirmDeleteDialogComponent, {
        width: '400px',
        data: {
          title: 'Confirmar eliminación',
          message: '¿Está seguro de que desea eliminar este registro?'
        }
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          this.loader.show('Eliminando registro...');
          this.personAcademicService.delete(this.idEncrypt, row.id.toString()).subscribe({
            next: (response) => {
              this.loader.hide();
              this.toastr.success('Registro eliminado correctamente', 'Éxito');
              this.getDataAcademic(this.idEncrypt);
            },
            error: (error) => {
              this.loader.hide();
              this.toastr.error('Error al eliminar');
            }
          });
        }
      });
  }

  sinExperiencia() {
    console.log('Sin experiencia');
    this.form.patchValue({
      nomInst: 'Sin estudios',
      programa: 'Sin estudios',
      mesIni: null,
      anoIni: null,
      mesFin: null,
      anoFin: null,
      actEst: false,
      idNivAca: '1'
    });
    this.createAcademicLevel();
  }

  setAcademicData(): MiAdmPersonasAcademicos {
    const isStudying = this.form.value.actEst;
    return {
      id: 0,
      idTipoIdentificacion: Number.parseInt(this.tipIde),
      identificacion: this.codEmp,
      mesInicio: Number.parseInt(this.form.value.mesIni!),
      anioInicio: Number.parseInt(this.form.value.anoIni!),
      mesFin: isStudying ? null : Number.parseInt(this.form.value.mesFin!),
      anioFin: isStudying ? null : Number.parseInt(this.form.value.anoFin!),
      actualmenteEstudiando: this.form.value.actEst!,
      idNivelAcademico: Number.parseInt(this.form.value.idNivAca!),
      institucion: this.form.value.nomInst!,
      programa: this.form.value.programa!,
      idEstado: 1
    };
  }

  createAcademicLevel() {
    if (this.form.valid) {
      this.loader.show('Guardando nivel académico...');
      const payload = this.setAcademicData();
      // console.log('Payload a enviar:', payload);
      this.personAcademicService.createFull(this.idEncrypt, payload).subscribe({
        next: (response) => {
          this.loader.hide();
          this.toastr.success('Nivel académico guardado con éxito');
          // this.closeDialog(true);
          this.getDataAcademic(this.idEncrypt);
        },
        error: (err) => {
          this.loader.hide();
          this.toastr.error('Error al guardar el nivel académico');
        }
      });
    } else {
      this.form.markAllAsTouched(); // Para mostrar errores
    }
  }

  private validateInexperiencia(data: MiAdmPersonasAcademicos[]): boolean {
    if (data.length === 0) return false;
    return data.some(
      (item) => (item.institucion && item.institucion.includes('Sin estudios')) || (item.programa && item.programa.includes('Sin estudios'))
    );
  }
}
